import {
  AdministrationRoles,
  BillingRoles,
  CalendarRoles,
  DirectoryRoles,
  DriveRoles,
  GPanelContactsPermissions,
  RulesEngineRoles,
  ToolsRoles,
  UserRoles,
  VaultRoles,
} from '../utils/roles/client-roles';
import { FeatureFlagName } from '../utils/feature-flags';

import { gPanelTiers } from './gPanel-tiers';

import AddToDrive from '@mui/icons-material/AddToDrive';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import AlternateEmailOutlined from '@mui/icons-material/AlternateEmailOutlined';
import AppsOutlined from '@mui/icons-material/AppsOutlined';
import CalendarToday from '@mui/icons-material/CalendarToday';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import Contacts from '@mui/icons-material/Contacts';
import ContactsOutlined from '@mui/icons-material/ContactsOutlined';
import Groups from '@mui/icons-material/Groups';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import MailOutlined from '@mui/icons-material/MailOutlined';
import Person from '@mui/icons-material/Person';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import PhoneIphoneOutlined from '@mui/icons-material/PhoneIphoneOutlined';
import SchemaOutlined from '@mui/icons-material/SchemaOutlined';
import Settings from '@mui/icons-material/Settings';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import History from '@mui/icons-material/History';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import People from '@mui/icons-material/People';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import Devices from '@mui/icons-material/Devices';
import ViewList from '@mui/icons-material/ViewList';
import Assessment from '@mui/icons-material/Assessment';
import SettingsSuggest from '@mui/icons-material/SettingsSuggest';
import ImportExport from '@mui/icons-material/ImportExport';
import AttachMoney from '@mui/icons-material/AttachMoney';
import AttachMoneyOutlined from '@mui/icons-material/AttachMoneyOutlined';
import SettingsApplications from '@mui/icons-material/SettingsApplications';
import PasswordOutlined from '@mui/icons-material/PasswordOutlined';
import DomainOutlined from '@mui/icons-material/DomainOutlined';
import StarBorder from '@mui/icons-material/StarBorder';
import EventNoteOutlined from '@mui/icons-material/EventNoteOutlined';
import PolicyRounded from '@mui/icons-material/PolicyRounded';
import PolicyOutlined from '@mui/icons-material/PolicyOutlined';
import PersonOff from '@mui/icons-material/PersonOff';
import PersonOffOutlined from '@mui/icons-material/PersonOffOutlined';
import Group from '@mui/icons-material/Group';
import ContactPhone from '@mui/icons-material/ContactPhone';
import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import AppBlockingOutlined from '@mui/icons-material/AppBlockingOutlined';
import Google from '@mui/icons-material/Google';
import Email from '@mui/icons-material/Email';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import HourglassBottomOutlined from '@mui/icons-material/HourglassBottomOutlined';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import CreditCardOutlined from '@mui/icons-material/CreditCardOutlined';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import ErrorOutlined from '@mui/icons-material/ErrorOutlined';
import SecurityOutlined from '@mui/icons-material/SecurityOutlined';
import Label from '@mui/icons-material/Label';
import DashboardCustomizeOutlined from '@mui/icons-material/DashboardCustomizeOutlined';
import Extension from '@mui/icons-material/Extension';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';
import WidgetsOutlined from '@mui/icons-material/WidgetsOutlined';

interface BasePageConfigItem {
  name: string;
  link: string;
  value: string;
  icon: React.ElementType;
  requiredPermission: string;
  requiredPermissionDisplayValue: string;
  roleGroup:
    | 'userRoles'
    | 'calendarRoles'
    | 'driveRoles'
    | 'administrationRoles'
    | 'directoryRoles'
    | 'gPanelContactsRoles'
    | 'changeLogRoles'
    | 'toolsRoles'
    | 'billingRoles'
    | 'rulesEngineRoles'
    | 'userViewRoles';
  requiredFeatureFlag?: FeatureFlagName;

  minimumTier: number;
  minimumTierDisplayValue: string;
}

interface PageConfigItem extends BasePageConfigItem {
  isUserDetails?: boolean;
  isGroupDetails?: boolean;
  isGmailSettings?: boolean;
  isCalendarDetails?: boolean;
  isDecomDetails?: boolean;
  isIndexingDetails?: boolean;
  isRoleDetails?: boolean;
  isAppSettings?: boolean;
  isBillingDetails?: boolean;
  isLayoutRoute?: boolean;
  isUserPortal?: boolean;
  isViewAdmin?: boolean;
  hiddenFromSearch?: boolean;
}

// THE KEY NEEDS TO MATCH THE VALUE PROPERTY
export const pagesConfig = {
  commandDashboard: {
    name: 'Dashboard',
    link: 'dashboard',
    value: 'commandDashboard',
    icon: DashboardCustomizeOutlined,
    requiredPermission: '*',
    requiredPermissionDisplayValue: '*',
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.enterprise.rank,
    minimumTierDisplayValue: gPanelTiers.enterprise.displayValue,
    requiredFeatureFlag: 'commandDashboardUi',
  },
  users: {
    name: 'User Management',
    link: 'users',
    value: 'users',
    icon: Person,
    requiredPermission: UserRoles.ViewUserManagement.value,
    requiredPermissionDisplayValue: UserRoles.ViewUserManagement.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
  },
  userDetails: {
    name: 'User Details',
    link: ':userEmail',
    value: 'userDetails',
    icon: Person,
    requiredPermission: UserRoles.ViewUserManagement.value,
    requiredPermissionDisplayValue: UserRoles.ViewUserManagement.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isLayoutRoute: true,
  },
  createUser: {
    name: 'Create User',
    link: 'create',
    value: 'createUser',
    icon: Person,
    requiredPermission: UserRoles.CreateUser.value,
    requiredPermissionDisplayValue: UserRoles.CreateUser.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    hiddenFromSearch: true,
  },
  gpanelContacts: {
    name: 'gPanel Contacts',
    link: 'gpanel-contacts',
    value: 'gpanelContacts',
    icon: Contacts,
    requiredPermission: GPanelContactsPermissions.CanViewGPanelContacts.value,
    requiredPermissionDisplayValue:
      GPanelContactsPermissions.CanViewGPanelContacts.name,
    roleGroup: 'gPanelContactsRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
  },
  generalSettings: {
    name: 'General Settings',
    link: 'general-settings',
    value: 'generalSettings',
    icon: SettingsOutlined,
    requiredPermission: '*',
    requiredPermissionDisplayValue: '*',
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
  },
  userDetailsRoles: {
    name: 'Roles',
    link: 'roles',
    value: 'userDetailsRoles',
    icon: AdminPanelSettingsOutlined,
    requiredPermission: UserRoles.ViewUserRolesTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewUserRolesTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.enterprise.rank,
    minimumTierDisplayValue: gPanelTiers.enterprise.displayValue,
    isUserDetails: true,
    requiredFeatureFlag: 'groupsGovernanceBackend',
  },
  aliases: {
    name: 'Aliases',
    link: 'aliases',
    value: 'aliases',
    icon: AlternateEmailOutlined,
    requiredPermission: UserRoles.ViewAliasesTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewAliasesTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
  },
  personalContacts: {
    name: 'Personal Contacts',
    link: 'personal-contacts',
    value: 'personalContacts',
    icon: ContactsOutlined,
    requiredPermission: UserRoles.ViewContactsTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewContactsTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    isUserDetails: true,
  },
  profileInformation: {
    name: 'Profile Information',
    link: 'profile-information',
    value: 'profileInformation',
    icon: InfoOutlined,
    requiredPermission: UserRoles.ViewProfileTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewProfileTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
  },
  customAttributes: {
    name: 'Custom Attributes',
    link: 'custom-attributes',
    value: 'customAttributes',
    icon: SchemaOutlined,
    requiredPermission: UserRoles.ViewUserCustomAttributes.value,
    requiredPermissionDisplayValue: UserRoles.ViewUserCustomAttributes.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
  },
  userDetailsGroups: {
    name: 'Groups',
    link: 'groups',
    value: 'userDetailsGroups',
    icon: GroupsOutlined,
    requiredPermission: UserRoles.ViewGroupsTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewGroupsTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
  },
  gmailSettings: {
    name: 'Gmail Settings',
    link: 'gmail-settings',
    value: 'gmailSettings',
    icon: MailOutlined,
    requiredPermission: UserRoles.ViewGmailTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewGmailTab.name,
    minimumTier: gPanelTiers.essentials.rank,
    roleGroup: 'userRoles',
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
  },
  accountDelegation: {
    name: 'Account Delegation',
    link: 'account-delegation',
    value: 'accountDelegation',
    icon: MailOutlined,
    requiredPermission: UserRoles.ViewGmailTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewGmailTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
    isGmailSettings: true,
  },
  filters: {
    name: 'Filters',
    link: 'filters',
    value: 'filters',
    icon: MailOutlined,
    requiredPermission: UserRoles.ViewGmailFilters.value,
    requiredPermissionDisplayValue: UserRoles.ViewGmailFilters.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
    isGmailSettings: true,
  },
  sendAs: {
    name: 'Send As',
    link: 'send-as',
    value: 'sendAs',
    icon: MailOutlined,
    requiredPermission: UserRoles.ViewGmailTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewGmailTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
    isGmailSettings: true,
  },
  labels: {
    name: 'Labels',
    link: 'labels',
    value: 'labels',
    icon: MailOutlined,
    requiredPermission: UserRoles.ViewGmailTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewGmailTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
    isGmailSettings: true,
  },
  popSettings: {
    name: 'POP Settings',
    link: 'pop-settings',
    value: 'popSettings',
    icon: MailOutlined,
    requiredPermission: UserRoles.ViewGmailTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewGmailTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
    isGmailSettings: true,
  },
  imapSettings: {
    name: 'IMAP Settings',
    link: 'imap-settings',
    value: 'imapSettings',
    icon: MailOutlined,
    requiredPermission: UserRoles.ViewGmailTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewGmailTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
    isGmailSettings: true,
  },
  vacationResponder: {
    name: 'Vacation Responder',
    link: 'vacation-responder',
    value: 'vacationResponder',
    icon: MailOutlined,
    requiredPermission: UserRoles.ViewGmailTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewGmailTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
    isGmailSettings: true,
  },
  forwarding: {
    name: 'Forwarding',
    link: 'forwarding',
    value: 'forwarding',
    icon: MailOutlined,
    requiredPermission: UserRoles.ViewGmailTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewGmailTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
    isGmailSettings: true,
  },
  forwardingAddresses: {
    name: 'Forwarding Addresses',
    link: 'forwarding-addresses',
    value: 'forwardingAddresses',
    icon: MailOutlined,
    requiredPermission: UserRoles.ViewGmailTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewGmailTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
    isGmailSettings: true,
  },
  signatures: {
    name: 'Signatures',
    link: 'signatures',
    value: 'signatures',
    icon: MailOutlined,
    requiredPermission: UserRoles.ViewGmailTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewGmailTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
    isGmailSettings: true,
  },
  userDetailsCalendars: {
    name: 'Calendars',
    link: 'calendars',
    value: 'userDetailsCalendars',
    icon: CalendarTodayOutlined,
    requiredPermission: UserRoles.ViewCalendarsTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewCalendarsTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    isUserDetails: true,
  },
  userDetailsDrive: {
    name: 'Drive',
    link: 'drive',
    value: 'userDetailsDrive',
    icon: AddToDrive,
    requiredPermission: UserRoles.ViewDriveTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewDriveTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    isUserDetails: true,
  },
  userDriveSearch: {
    name: 'Drive Search',
    link: 'drive-search',
    value: 'userDriveSearch',
    icon: AddToDrive,
    requiredPermission: UserRoles.ViewDriveSearchTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewDriveSearchTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    isUserDetails: true,
  },
  auditing: {
    name: 'Auditing',
    link: 'auditing',
    value: 'auditing',
    icon: AdminPanelSettingsOutlined,
    requiredPermission: UserRoles.ViewAuditingTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewAuditingTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    isUserDetails: true,
  },
  userDetailsMobileDevices: {
    name: 'Mobile Devices',
    link: 'mobile-devices',
    value: 'userDetailsMobileDevices',
    icon: PhoneIphoneOutlined,
    requiredPermission: UserRoles.ViewDevicesTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewDevicesTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    isUserDetails: true,
  },
  appPasswords: {
    name: 'App Passwords',
    link: 'app-passwords',
    value: 'appPasswords',
    icon: PasswordOutlined,
    requiredPermission: UserRoles.ViewAppPasswordsTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewAppPasswordsTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
  },
  thirdPartyApps: {
    name: 'Third Party Applications',
    link: 'third-party-apps',
    value: 'thirdPartyApps',
    icon: AppsOutlined,
    requiredPermission: UserRoles.ViewThirdPartyAppsTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewThirdPartyAppsTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
  },
  userDetailsHistory: {
    name: 'History',
    link: 'history',
    value: 'userDetailsHistory',
    icon: HistoryOutlined,
    requiredPermission: UserRoles.ViewHistoryTab.value,
    requiredPermissionDisplayValue: UserRoles.ViewHistoryTab.name,
    roleGroup: 'userRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isUserDetails: true,
  },
  groupDetails: {
    name: 'Group Details',
    link: ':groupEmail',
    value: 'groupDetails',
    icon: Groups,
    requiredPermission: DirectoryRoles.ViewGroups.value,
    requiredPermissionDisplayValue: DirectoryRoles.ViewGroups.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isLayoutRoute: true,
  },
  groups: {
    name: 'Groups',
    link: 'groups',
    value: 'groups',
    icon: People,
    requiredPermission: DirectoryRoles.ViewGroups.value,
    requiredPermissionDisplayValue: DirectoryRoles.ViewGroups.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
  },
  groupsGeneralInformation: {
    name: 'General Information',
    link: 'general-information',
    value: 'groupsGeneralInformation',
    icon: InfoOutlined,
    requiredPermission: DirectoryRoles.ViewGroups.value,
    requiredPermissionDisplayValue: DirectoryRoles.ViewGroups.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isGroupDetails: true,
  },
  businessGroupSettings: {
    name: 'Business Group Settings',
    link: 'business-group-settings',
    value: 'businessGroupSettings',
    icon: SettingsOutlined,
    requiredPermission: DirectoryRoles.ViewGroups.value,
    requiredPermissionDisplayValue: DirectoryRoles.ViewGroups.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isGroupDetails: true,
  },
  groupSecuritySettings: {
    name: 'Security Settings',
    link: 'group-security-settings',
    value: 'groupSecuritySettings',
    icon: SecurityOutlined,
    requiredPermission: DirectoryRoles.ViewGroups.value,
    requiredPermissionDisplayValue: DirectoryRoles.ViewGroups.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isGroupDetails: true,
  },
  groupMembership: {
    name: 'Group Members',
    link: 'membership',
    value: 'membership',
    icon: GroupsOutlined,
    requiredPermission: DirectoryRoles.ViewGroups.value,
    requiredPermissionDisplayValue: DirectoryRoles.ViewGroups.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isGroupDetails: true,
  },
  groupAliases: {
    name: 'Group Aliases',
    link: 'aliases',
    value: 'groupAliases',
    icon: AlternateEmailOutlined,
    requiredPermission: DirectoryRoles.ViewGroups.value,
    requiredPermissionDisplayValue: DirectoryRoles.ViewGroups.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isGroupDetails: true,
  },
  groupLogs: {
    name: 'Group Logs',
    link: 'logs',
    value: 'logs',
    icon: HistoryOutlined,
    requiredPermission: DirectoryRoles.ViewHistoryTab.value,
    requiredPermissionDisplayValue: DirectoryRoles.ViewHistoryTab.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isGroupDetails: true,
  },
  groupTemplates: {
    name: 'Group Templates',
    link: 'group-templates',
    value: 'groupTemplates',
    icon: People,
    requiredPermission: DirectoryRoles.ViewGroupTemplates.value,
    requiredPermissionDisplayValue: DirectoryRoles.ViewGroupTemplates.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  createGroupTemplate: {
    name: 'Create Group Template',
    link: 'create',
    value: 'createGroupTemplate',
    icon: People,
    requiredPermission: DirectoryRoles.CreateGroupTemplate.value,
    requiredPermissionDisplayValue: DirectoryRoles.CreateGroupTemplate.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  editGroupTemplate: {
    name: 'Edit Group Template',
    link: 'edit/:id',
    value: 'editGroupTemplate',
    icon: People,
    requiredPermission: DirectoryRoles.EditGroupTemplates.value,
    requiredPermissionDisplayValue: DirectoryRoles.EditGroupTemplates.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  orgUnits: {
    name: 'Organizational Units',
    link: 'organizational-units',
    value: 'orgUnits',
    icon: People,
    requiredPermission: DirectoryRoles.ViewOrgUnits.value,
    requiredPermissionDisplayValue: DirectoryRoles.ViewOrgUnits.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  sharedContacts: {
    name: 'Shared Contacts',
    link: 'shared-contacts',
    value: 'sharedContacts',
    icon: Contacts,
    requiredPermission: DirectoryRoles.ViewSharedContacts.value,
    requiredPermissionDisplayValue: DirectoryRoles.ViewSharedContacts.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  contactSync: {
    name: 'Contact Sync',
    link: 'contact-sync',
    value: 'contactSync',
    icon: Contacts,
    requiredPermission: DirectoryRoles.ViewContactSync.value,
    requiredPermissionDisplayValue: DirectoryRoles.ViewContactSync.name,
    roleGroup: 'directoryRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  calendars: {
    name: 'Calendars',
    link: 'calendars',
    value: 'calendars',
    icon: CalendarMonth,
    requiredPermission: CalendarRoles.ViewCalendars.value,
    requiredPermissionDisplayValue: CalendarRoles.ViewCalendars.name,
    roleGroup: 'calendarRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  resourceCalendarsLayout: {
    name: 'Resource Calendars',
    link: 'resource-calendars',
    value: 'resourceCalendarsLayout',
    icon: CalendarToday,
    requiredPermission: '*',
    requiredPermissionDisplayValue: '*',
    roleGroup: 'calendarRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    isLayoutRoute: true,
  },
  resourceCalendars: {
    name: 'Resource Calendars',
    link: 'resource-calendars',
    value: 'resourceCalendars',
    icon: CalendarTodayOutlined,
    requiredPermission: CalendarRoles.ViewResourceCalendars.value,
    requiredPermissionDisplayValue: CalendarRoles.ViewResourceCalendars.name,
    roleGroup: 'calendarRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    isCalendarDetails: true,
  },
  createResourceCalendar: {
    name: 'Create Resource Calendar',
    link: 'create',
    value: 'createResourceCalendar',
    icon: CalendarToday,
    requiredPermission: CalendarRoles.CreateResourceCalendar.value,
    requiredPermissionDisplayValue: CalendarRoles.CreateResourceCalendar.name,
    roleGroup: 'calendarRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    isCalendarDetails: false,
    hiddenFromSearch: true,
  },
  editResourceCalendar: {
    name: 'Edit Resource Calendar',
    link: 'edit/:id',
    value: 'editResourceCalendar',
    icon: CalendarToday,
    requiredPermission: CalendarRoles.EditResourceCalendar.value,
    requiredPermissionDisplayValue: CalendarRoles.EditResourceCalendar.name,
    roleGroup: 'calendarRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    isCalendarDetails: false,
    hiddenFromSearch: true,
  },

  resourceBuildings: {
    name: 'Buildings',
    link: 'buildings',
    value: 'resourceBuildings',
    icon: DomainOutlined,
    requiredPermission: CalendarRoles.ViewResourcesByBuildings.value,
    requiredPermissionDisplayValue: CalendarRoles.ViewResourcesByBuildings.name,
    roleGroup: 'calendarRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    isCalendarDetails: true,
  },
  resourceFeatures: {
    name: 'Features',
    link: 'features',
    value: 'resourceFeatures',
    icon: StarBorder,
    requiredPermission: CalendarRoles.ViewFeatures.value,
    requiredPermissionDisplayValue: CalendarRoles.ViewFeatures.name,
    roleGroup: 'calendarRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    isCalendarDetails: true,
  },
  resourceDefaults: {
    name: 'Domain Defaults',
    link: 'domain-defaults',
    value: 'resourceDefaults',
    icon: EventNoteOutlined,
    requiredPermission:
      AdministrationRoles.ViewDomainDefaultResourceCalendar.value,
    requiredPermissionDisplayValue:
      AdministrationRoles.ViewDomainDefaultResourceCalendar.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    isCalendarDetails: true,
  },
  resourceTemplates: {
    name: 'Resource Templates',
    link: 'resource-templates',
    value: 'resourceTemplates',
    icon: CalendarMonth,
    requiredPermission: CalendarRoles.ViewResourceTemplates.value,
    requiredPermissionDisplayValue: CalendarRoles.ViewResourceTemplates.name,
    roleGroup: 'calendarRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  createResourceTemplate: {
    name: 'Create Resource Template',
    link: 'create',
    value: 'createResourceTemplate',
    icon: CalendarMonth,
    requiredPermission: CalendarRoles.CreateResourceTemplate.value,
    requiredPermissionDisplayValue: CalendarRoles.CreateResourceTemplate.name,
    roleGroup: 'calendarRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  editResourceTemplate: {
    name: 'Edit Resource Template',
    link: 'edit/:id',
    value: 'editResourceTemplate',
    icon: CalendarMonth,
    requiredPermission: CalendarRoles.EditResourceTemplate.value,
    requiredPermissionDisplayValue: CalendarRoles.EditResourceTemplate.name,
    roleGroup: 'calendarRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  calendarTransferLogs: {
    name: 'Calendar Transfer Logs',
    link: 'calendar-transfer-logs',
    value: 'calendarTransferLogs',
    icon: CalendarMonth,
    requiredPermission: CalendarRoles.ViewTransferOwnershipLogs.value,
    requiredPermissionDisplayValue:
      CalendarRoles.ViewTransferOwnershipLogs.name,
    roleGroup: 'calendarRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  driveExplorer: {
    name: 'Drive Explorer',
    link: 'drive-explorer',
    value: 'driveExplorer',
    icon: AddToDrive,
    requiredPermission: DriveRoles.ViewDriveExplorer.value,
    requiredPermissionDisplayValue: DriveRoles.ViewDriveExplorer.name,
    roleGroup: 'driveRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  driveSearch: {
    name: 'Drive Search',
    link: 'drive-search',
    value: 'driveSearch',
    icon: AddToDrive,
    requiredPermission: DriveRoles.ViewDriveSearch.value,
    requiredPermissionDisplayValue: DriveRoles.ViewDriveSearch.name,
    roleGroup: 'driveRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  ownershipTransferLogs: {
    name: 'Ownership Transfer Logs',
    link: 'ownership-transfer-logs',
    value: 'ownershipTransferLogs',
    icon: AddToDrive,
    requiredPermission: DriveRoles.ViewOwnershipTransferLogs.value,
    requiredPermissionDisplayValue: DriveRoles.ViewOwnershipTransferLogs.name,
    roleGroup: 'driveRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  driveSweep: {
    name: 'Drive Sweep',
    link: 'drive-sweep',
    value: 'driveSweep',
    icon: AddToDrive,
    requiredPermission: DriveRoles.ViewDriveSweep.value,
    requiredPermissionDisplayValue: DriveRoles.ViewDriveSweep.name,
    roleGroup: 'driveRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  createDriveSweepFormPage: {
    name: 'Create Drive Sweep',
    link: 'create',
    value: 'createDriveSweepFormPage',
    icon: AddToDrive,
    requiredPermission: DriveRoles.CanCreateDriveSweep.value,
    requiredPermissionDisplayValue: DriveRoles.CanCreateDriveSweep.value,
    roleGroup: 'driveRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  canCreateDriveSweepFromPage: {
    name: 'Start Drive Sweep',
    link: 'start',
    value: 'startDriveSweepFormPage',
    icon: AddToDrive,
    requiredPermission: DriveRoles.CanStartDriveSweep.value,
    requiredPermissionDisplayValue: DriveRoles.CanStartDriveSweep.value,
    roleGroup: 'driveRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  sharedDrives: {
    name: 'Shared Drives',
    link: 'shared-drives',
    value: 'sharedDrives',
    icon: AddToDrive,
    requiredPermission: DriveRoles.ViewSharedDrives.value,
    requiredPermissionDisplayValue: DriveRoles.ViewSharedDrives.name,
    roleGroup: 'driveRoles',
    minimumTier: gPanelTiers.standard.rank,
    minimumTierDisplayValue: gPanelTiers.standard.displayValue,
  },
  mobileManagement: {
    name: 'Mobile Management',
    link: 'mobile-management',
    value: 'mobileManagement',
    icon: Devices,
    requiredPermission: ToolsRoles.ViewMobileManagement.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewMobileManagement.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  chromeDevices: {
    name: 'Chrome Devices',
    link: 'chrome-devices',
    value: 'chromeDevices',
    icon: Devices,
    requiredPermission: ToolsRoles.ViewDeviceManagement.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewDeviceManagement.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  logging: {
    name: 'Logging',
    link: 'logging',
    value: 'logging',
    icon: ViewList,
    requiredPermission: AdministrationRoles.ViewAppLogs.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewAppLogs.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
  },
  reports: {
    name: 'Reports',
    link: 'reports',
    value: 'reports',
    icon: Assessment,
    requiredPermission: ToolsRoles.ViewReports.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewReports.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  createReport: {
    name: 'Create Report',
    link: 'create',
    value: 'createReport',
    icon: Assessment,
    requiredPermission: ToolsRoles.CreateReport.value,
    requiredPermissionDisplayValue: ToolsRoles.CreateReport.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  editReport: {
    name: 'Edit Report',
    link: 'edit/:id',
    value: 'editReport',
    icon: Assessment,
    requiredPermission: ToolsRoles.EditReport.value,
    requiredPermissionDisplayValue: ToolsRoles.EditReport.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  cPanelLogs: {
    name: 'cPanel Logs',
    link: 'cpanel-logs',
    value: 'cPanelLogs',
    icon: Assessment,
    requiredPermission: ToolsRoles.ViewCPanelLogs.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewCPanelLogs.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.standard.rank,
    minimumTierDisplayValue: gPanelTiers.standard.displayValue,
  },
  rulesEngine: {
    name: 'Rules Engine',
    link: 'rules-engine',
    value: 'rulesEngine',
    icon: SettingsSuggest,
    requiredPermission: RulesEngineRoles.ViewRulesGrid.value,
    requiredPermissionDisplayValue: RulesEngineRoles.ViewRulesGrid.name,
    roleGroup: 'rulesEngineRoles',
    minimumTier: gPanelTiers.enterprise.rank,
    minimumTierDisplayValue: gPanelTiers.enterprise.displayValue,
  },
  createRule: {
    name: 'Create Rule',
    link: 'create',
    value: 'createRule',
    icon: SettingsSuggest,
    requiredPermission: RulesEngineRoles.AddRules.value,
    requiredPermissionDisplayValue: RulesEngineRoles.AddRules.name,
    roleGroup: 'rulesEngineRoles',
    minimumTier: gPanelTiers.enterprise.rank,
    minimumTierDisplayValue: gPanelTiers.enterprise.displayValue,
    hiddenFromSearch: true,
  },
  browseTemplates: {
    name: 'Browse Templates',
    link: 'browse-templates',
    value: 'rulesEngine',
    icon: SettingsSuggest,
    requiredPermission: RulesEngineRoles.AddRules.value,
    requiredPermissionDisplayValue: RulesEngineRoles.AddRules.name,
    roleGroup: 'rulesEngineRoles',
    minimumTier: gPanelTiers.enterprise.rank,
    minimumTierDisplayValue: gPanelTiers.enterprise.displayValue,
    hiddenFromSearch: true,
  },
  editRule: {
    name: 'Edit Rule',
    link: 'edit',
    value: 'editRule',
    icon: SettingsSuggest,
    requiredPermission: RulesEngineRoles.EditRules.value,
    requiredPermissionDisplayValue: RulesEngineRoles.EditRules.name,
    roleGroup: 'rulesEngineRoles',
    minimumTier: gPanelTiers.enterprise.rank,
    minimumTierDisplayValue: gPanelTiers.enterprise.displayValue,
    hiddenFromSearch: true,
  },
  cloneRule: {
    name: 'Clone Rule',
    link: 'clone',
    value: 'cloneRule',
    icon: SettingsSuggest,
    requiredPermission: RulesEngineRoles.AddRules.value,
    requiredPermissionDisplayValue: RulesEngineRoles.AddRules.name,
    roleGroup: 'rulesEngineRoles',
    minimumTier: gPanelTiers.enterprise.rank,
    minimumTierDisplayValue: gPanelTiers.enterprise.displayValue,
    hiddenFromSearch: true,
  },
  policies: {
    name: 'Policies',
    link: 'policies',
    value: 'policies',
    icon: SettingsSuggest,
    requiredPermission: ToolsRoles.ViewPolicies.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewPolicies.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  createPolicy: {
    name: 'Create Policy',
    link: 'create',
    value: 'createPolicy',
    icon: SettingsSuggest,
    requiredPermission: ToolsRoles.CreatePolicy.value,
    requiredPermissionDisplayValue: ToolsRoles.CreatePolicy.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  editPolicy: {
    name: 'Edit Policy',
    link: 'edit/:id',
    value: 'editPolicy',
    icon: SettingsSuggest,
    requiredPermission: ToolsRoles.EditPolicy.value,
    requiredPermissionDisplayValue: ToolsRoles.EditPolicy.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  clonePolicy: {
    name: 'Clone Policy',
    link: 'clone/:id',
    value: 'clonePolicy',
    icon: SettingsSuggest,
    requiredPermission: ToolsRoles.CopyPolicy.value,
    requiredPermissionDisplayValue: ToolsRoles.CopyPolicy.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  decommissioningLayout: {
    name: 'Decommissioning',
    link: 'decommissioning',
    value: 'decommissioningLayout',
    icon: PolicyRounded,
    requiredPermission: ToolsRoles.ViewDecommissioning.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewDecommissioning.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.standard.rank,
    minimumTierDisplayValue: gPanelTiers.standard.displayValue,
    isLayoutRoute: true,
  },
  createDecomPolicyFormPage: {
    name: 'New Decommissioning Policy',
    link: 'create',
    value: 'createDecomPolicyFormPage',
    icon: PolicyRounded,
    requiredPermission: ToolsRoles.CreateDecommissioningProcesses.value,
    requiredPermissionDisplayValue:
      ToolsRoles.CreateDecommissioningProcesses.value,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.standard.rank,
    minimumTierDisplayValue: gPanelTiers.standard.displayValue,
    hiddenFromSearch: true,
  },
  editDecomPolicyFormPage: {
    name: 'Edit Decommissioning Policy',
    link: 'edit/:id',
    value: 'editDecomPolicyFormPage',
    icon: PolicyRounded,
    requiredPermission: ToolsRoles.EditDecommissioningProcesses.value,
    requiredPermissionDisplayValue:
      ToolsRoles.EditDecommissioningProcesses.value,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.standard.rank,
    minimumTierDisplayValue: gPanelTiers.standard.displayValue,
    hiddenFromSearch: true,
  },
  cloneDecomPolicyFormPage: {
    name: 'Clone Decommissioning Policy',
    link: 'clone/:id',
    value: 'cloneDecomPolicyFormPage',
    icon: PolicyRounded,
    requiredPermission: ToolsRoles.CreateDecommissioningProcesses.value,
    requiredPermissionDisplayValue:
      ToolsRoles.CreateDecommissioningProcesses.value,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.standard.rank,
    minimumTierDisplayValue: gPanelTiers.standard.displayValue,
    hiddenFromSearch: true,
  },
  decomPolicies: {
    name: 'Decom Policies',
    link: 'policies',
    value: 'decomPolicies',
    icon: PolicyOutlined,
    requiredPermission: ToolsRoles.ViewDecommissioning.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewDecommissioning.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.standard.rank,
    minimumTierDisplayValue: gPanelTiers.standard.displayValue,
    isDecomDetails: true,
  },
  decomSuspendedUsers: {
    name: 'Decom Suspended Users',
    link: 'suspended-users',
    value: 'decomSuspendedUsers',
    icon: PersonOffOutlined,
    requiredPermission: ToolsRoles.ViewDecommissioning.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewDecommissioning.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.standard.rank,
    minimumTierDisplayValue: gPanelTiers.standard.displayValue,
    isDecomDetails: true,
  },
  decomLogs: {
    name: 'Decom Logs',
    link: 'logs',
    value: 'decomLogs',
    icon: History,
    requiredPermission: ToolsRoles.ViewDecommissioning.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewDecommissioning.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.standard.rank,
    minimumTierDisplayValue: gPanelTiers.standard.displayValue,
    isDecomDetails: true,
  },
  signatureTemplates: {
    name: 'Signature Templates',
    link: 'signature-templates',
    value: 'signatureTemplates',
    icon: SettingsSuggest,
    requiredPermission: ToolsRoles.ViewSignatureTemplates.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewSignatureTemplates.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  createSignatureTemplate: {
    name: 'Create Signature Template',
    link: 'create',
    value: 'createSignatureTemplate',
    icon: SettingsSuggest,
    requiredPermission: ToolsRoles.CreateTemplates.value,
    requiredPermissionDisplayValue: ToolsRoles.CreateTemplates.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  editSignatureTemplate: {
    name: 'Edit Signature Template',
    link: 'edit/:id',
    value: 'editSignatureTemplate',
    icon: SettingsSuggest,
    requiredPermission: ToolsRoles.EditTemplate.value,
    requiredPermissionDisplayValue: ToolsRoles.EditTemplate.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  cloneSignatureTemplate: {
    name: 'Clone Signature Template',
    link: 'clone/:id',
    value: 'cloneSignatureTemplate',
    icon: SettingsSuggest,
    requiredPermission: ToolsRoles.CreateTemplates.value,
    requiredPermissionDisplayValue: ToolsRoles.CreateTemplates.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  bulkOps: {
    name: 'Bulk Operations',
    link: 'bulk-operations',
    value: 'bulkOps',
    icon: ImportExport,
    requiredPermission: ToolsRoles.ViewBulkImports.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewBulkImports.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  createBulkOps: {
    name: 'Create Bulk Operation',
    link: 'create',
    value: 'createBulkOps',
    icon: ImportExport,
    requiredPermission: ToolsRoles.ViewBulkImports.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewBulkImports.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  gmailExport: {
    name: 'Gmail Export',
    link: 'gmail-export',
    value: 'gmailExport',
    icon: ImportExport,
    requiredPermission: ToolsRoles.ViewGmailExport.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewGmailExport.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  vaultExport: {
    name: 'Vault',
    link: 'vault-export',
    value: 'vaultExport',
    icon: ImportExport,
    requiredPermission: VaultRoles.ViewVaultExports.value,
    requiredPermissionDisplayValue: VaultRoles.ViewVaultExports.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
  },
  gmailDeletion: {
    name: 'Gmail Deletion',
    link: 'gmail-deletion',
    value: 'gmailDeletion',
    icon: ImportExport,
    requiredPermission: ToolsRoles.ViewGmailSearch.value,
    requiredPermissionDisplayValue: ToolsRoles.ViewGmailSearch.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  createGmailDeletionFormPage: {
    name: 'Create Gmail Deletion',
    link: 'create',
    value: 'createGmailDeletion',
    icon: ImportExport,
    requiredPermission: ToolsRoles.RunGmailSearch.value,
    requiredPermissionDisplayValue: ToolsRoles.RunGmailSearch.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  editGmailDeletionFormPage: {
    name: 'Edit Gmail Deletion',
    link: 'edit/:id',
    value: 'editGmailDeletion',
    icon: ImportExport,
    requiredPermission: ToolsRoles.RunGmailSearch.value,
    requiredPermissionDisplayValue: ToolsRoles.RunGmailSearch.name,
    roleGroup: 'toolsRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  indexing: {
    name: 'Indexing',
    link: 'indexing',
    value: 'indexing',
    icon: Settings,
    requiredPermission: AdministrationRoles.ViewIndexing.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewIndexing.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isLayoutRoute: true,
  },
  indexingUsers: {
    name: 'Users',
    link: 'users',
    value: 'indexingUsers',
    icon: PersonOutlined,
    requiredPermission: AdministrationRoles.ViewIndexing.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewIndexing.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isIndexingDetails: true,
  },
  indexingGroups: {
    name: 'Groups',
    link: 'groups',
    value: 'indexingGroups',
    icon: PeopleOutlined,
    requiredPermission: AdministrationRoles.ViewIndexing.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewIndexing.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isIndexingDetails: true,
  },
  indexingCalendars: {
    name: 'Calendars',
    link: 'calendars',
    value: 'indexingCalendars',
    icon: CalendarMonthOutlined,
    requiredPermission: AdministrationRoles.ViewIndexing.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewIndexing.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isIndexingDetails: true,
  },
  indexingOrgs: {
    name: 'Org Units',
    link: 'org-units',
    value: 'indexingOrgs',
    icon: DomainOutlined,
    requiredPermission: AdministrationRoles.ViewIndexing.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewIndexing.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isIndexingDetails: true,
  },
  labs: {
    name: 'Labs',
    link: 'labs',
    value: 'labs',
    icon: Settings,
    requiredPermission: AdministrationRoles.ViewLabs.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewLabs.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  roleManager: {
    name: 'Role Manager',
    link: 'role-manager',
    value: 'roleManager',
    icon: AdminPanelSettings,
    requiredPermission: AdministrationRoles.ViewRoles.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewRoles.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
  },
  roleManagerDetails: {
    name: 'Role Manager Details',
    link: ':roleName',
    value: 'roleManagerDetails',
    icon: AdminPanelSettings,
    requiredPermission: AdministrationRoles.ViewRoles.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewRoles.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isLayoutRoute: true,
  },
  roleManagerPermissions: {
    name: 'Permissions',
    link: 'permissions',
    value: 'roleManagerPermissions',
    icon: AdminPanelSettings,
    requiredPermission: AdministrationRoles.EditRole.value,
    requiredPermissionDisplayValue: AdministrationRoles.EditRole.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isRoleDetails: true,
  },
  roleManagerMembership: {
    name: 'Role Members',
    link: 'membership',
    value: 'roleManagerMembership',
    icon: Group,
    requiredPermission: AdministrationRoles.EditRole.value,
    requiredPermissionDisplayValue: AdministrationRoles.EditRole.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isRoleDetails: true,
  },
  roleManagerSettings: {
    name: 'Role Settings',
    link: 'settings',
    value: 'roleManagerSettings',
    icon: Settings,
    requiredPermission: AdministrationRoles.EditRole.value,
    requiredPermissionDisplayValue: AdministrationRoles.EditRole.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isRoleDetails: true,
  },
  roleManagerLimitedVisibility: {
    name: 'Limited User Visibility',
    link: 'limited-user-visibility',
    value: 'roleManagerLimitedVisibility',
    icon: PersonOff,
    requiredPermission: AdministrationRoles.EditRole.value,
    requiredPermissionDisplayValue: AdministrationRoles.EditRole.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isRoleDetails: true,
  },
  roleManagerDirectorySearch: {
    name: 'User Portal Directory Search',
    link: 'directory-search',
    value: 'roleManagerDirectorySearch',
    icon: ContactPhone,
    requiredPermission: AdministrationRoles.EditRole.value,
    requiredPermissionDisplayValue: AdministrationRoles.EditRole.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isRoleDetails: true,
  },
  roleManagerLogs: {
    name: 'Logs',
    link: 'logs',
    value: 'roleManagerLogs',
    icon: History,
    requiredPermission: AdministrationRoles.ViewAppLogs.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewAppLogs.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isRoleDetails: true,
  },
  driveLabelsManager: {
    name: 'Label Manager',
    link: 'label-manager',
    value: 'driveLabelsManager',
    icon: Label,
    requiredPermission: AdministrationRoles.ViewLabelsManager.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewLabelsManager.name,
    requiredFeatureFlag: 'driveLabelsRead',
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
  },
  createDriveLabel: {
    name: 'Create Label',
    link: 'label-manager/create',
    value: 'createDriveLabel',
    icon: Label,
    requiredPermission: DriveRoles.CreateDriveLabels.value,
    requiredPermissionDisplayValue: DriveRoles.CreateDriveLabels.name,
    requiredFeatureFlag: 'driveLabelsQ4',
    roleGroup: 'driveRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  cloneDriveLabel: {
    name: 'Clone Label',
    link: 'label-manager/clone/:id',
    value: 'cloneDriveLabel',
    icon: Label,
    requiredPermission: DriveRoles.CreateDriveLabels.value,
    requiredPermissionDisplayValue: DriveRoles.CreateDriveLabels.name,
    requiredFeatureFlag: 'driveLabelsQ4',
    roleGroup: 'driveRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  editDriveLabel: {
    name: 'Edit Label',
    link: 'label-manager/edit/:labelId',
    value: 'editDriveLabel',
    icon: Label,
    requiredPermission: DriveRoles.EditDriveLabels.value,
    requiredPermissionDisplayValue: DriveRoles.EditDriveLabels.name,
    requiredFeatureFlag: 'driveLabelsQ4',
    roleGroup: 'driveRoles',
    minimumTier: gPanelTiers.starter.rank,
    minimumTierDisplayValue: gPanelTiers.starter.displayValue,
    hiddenFromSearch: true,
  },
  api: {
    name: 'gPanel API',
    link: 'gpanel-api',
    value: 'api',
    icon: Settings,
    requiredPermission: AdministrationRoles.ViewgPanelApi.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewgPanelApi.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.enterprise.rank,
    minimumTierDisplayValue: gPanelTiers.enterprise.displayValue,
  },
  settings: {
    name: 'Settings',
    link: 'settings',
    value: 'settings',
    icon: Settings,
    requiredPermission: AdministrationRoles.ViewSettings.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewSettings.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isLayoutRoute: true,
  },
  setGpanelAdmin: {
    name: 'Set gPanel Admin',
    link: 'gpanel-admin',
    value: 'setGpanelAdmin',
    icon: AdminPanelSettingsOutlined,
    requiredPermission: AdministrationRoles.ViewSetGpanelAdmin.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewSetGpanelAdmin.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isAppSettings: true,
  },
  ipIntrusion: {
    name: 'IP Intrusion',
    link: 'ip-intrusion',
    value: 'ipIntrusion',
    icon: AppBlockingOutlined,
    requiredPermission: AdministrationRoles.ViewIpIntrusion.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewIpIntrusion.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isAppSettings: true,
  },
  secondaryDomains: {
    name: 'Secondary Domains',
    link: 'secondary-domains',
    value: 'secondaryDomains',
    icon: AlternateEmailOutlined,
    requiredPermission: AdministrationRoles.ViewSecondaryDomains.value,
    requiredPermissionDisplayValue:
      AdministrationRoles.ViewSecondaryDomains.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isAppSettings: true,
  },
  timezone: {
    name: 'Timezone',
    link: 'timezone',
    value: 'timezone',
    icon: AccessTimeOutlined,
    requiredPermission: AdministrationRoles.ViewTimezone.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewTimezone.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isAppSettings: true,
  },
  customLabeling: {
    name: 'Custom Labeling',
    link: 'custom-labeling',
    value: 'customLabeling',
    icon: ImageOutlined,
    requiredPermission: AdministrationRoles.ViewCustomLabeling.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewCustomLabeling.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isAppSettings: true,
  },
  googleApiStatus: {
    name: 'Google API Status',
    link: 'google-api-status',
    value: 'googleApiStatus',
    icon: Google,
    requiredPermission: AdministrationRoles.ViewGoogleApiStatus.value,
    requiredPermissionDisplayValue:
      AdministrationRoles.ViewGoogleApiStatus.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isAppSettings: true,
  },
  sessionTimeout: {
    name: 'Session Timeout',
    link: 'session-timeout',
    value: 'sessionTimeout',
    icon: HourglassBottomOutlined,
    requiredPermission: AdministrationRoles.ViewSessionTimeout.value,
    requiredPermissionDisplayValue: AdministrationRoles.ViewSessionTimeout.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isAppSettings: true,
  },
  welcomeTemplates: {
    name: 'Welcome Templates',
    link: 'welcome-templates',
    value: 'welcomeTemplates',
    icon: EmailOutlined,
    requiredPermission: AdministrationRoles.ViewWelcomeTemplates.value,
    requiredPermissionDisplayValue:
      AdministrationRoles.ViewWelcomeTemplates.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isAppSettings: true,
  },
  integrations: {
    name: 'Integrations',
    link: 'integrations',
    value: 'integrations',
    icon: ExtensionOutlined,
    requiredPermission: AdministrationRoles.ViewSlackIntegration.value,
    requiredPermissionDisplayValue:
      AdministrationRoles.ViewSlackIntegration.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isAppSettings: true,
    hiddenFromSearch: true,
    requiredFeatureFlag: 'slackIntegration',
  },
  manageSlackChannels: {
    name: 'Slack',
    link: 'slack',
    value: 'manageSlackChannels',
    icon: Extension,
    requiredPermission: AdministrationRoles.ViewSlackIntegration.value,
    requiredPermissionDisplayValue:
      AdministrationRoles.ViewSlackIntegration.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    hiddenFromSearch: true,
    requiredFeatureFlag: 'slackIntegration',
  },

  welcomeTemplatesFormPage: {
    name: 'Create Welcome Template',
    link: 'create',
    value: 'welcomeTemplatesFormPage',
    icon: Email,
    requiredPermission: AdministrationRoles.CreateWelcomeTemplates.value,
    requiredPermissionDisplayValue:
      AdministrationRoles.CreateWelcomeTemplates.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isAppSettings: false,
    hiddenFromSearch: true,
  },
  editWelcomeTemplatesFormPage: {
    name: 'Edit Welcome Template',
    link: 'edit/:id',
    value: 'editWelcomeTemplatesFormPage',
    icon: Email,
    requiredPermission: AdministrationRoles.EditWelcomeTemplate.value,
    requiredPermissionDisplayValue:
      AdministrationRoles.EditWelcomeTemplate.name,
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isAppSettings: false,
    hiddenFromSearch: true,
  },
  billing: {
    name: 'Billing',
    link: 'billing',
    value: 'billing',
    icon: AttachMoney,
    requiredPermission: BillingRoles.viewMainBillingPage.value,
    requiredPermissionDisplayValue: BillingRoles.viewMainBillingPage.name,
    roleGroup: 'billingRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isLayoutRoute: true,
  },

  billingSubscriptions: {
    name: 'Subscriptions',
    link: 'subscriptions',
    value: 'billingSubscriptions',
    icon: ShoppingCartOutlined,
    requiredPermission: BillingRoles.viewMainBillingPage.value,
    requiredPermissionDisplayValue: BillingRoles.viewMainBillingPage.name,
    roleGroup: 'billingRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isBillingDetails: true,
  },
  addLicense: {
    name: 'Add License',
    link: 'add/:id',
    value: 'addLicense',
    icon: AttachMoneyOutlined,
    requiredPermission: BillingRoles.viewMainBillingPage.value,
    requiredPermissionDisplayValue: BillingRoles.viewMainBillingPage.name,
    roleGroup: 'billingRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    hiddenFromSearch: true,
  },
  billingPayments: {
    name: 'Payments',
    link: 'payments',
    value: 'billingPayments',
    icon: CreditCardOutlined,
    requiredPermission: BillingRoles.canViewPaymentMethods.value,
    requiredPermissionDisplayValue: BillingRoles.canViewPaymentMethods.name,
    roleGroup: 'billingRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isBillingDetails: true,
  },
  billingContacts: {
    name: 'Billing Contacts',
    link: 'contacts',
    value: 'billingContacts',
    icon: ContactsOutlined,
    requiredPermission: BillingRoles.canViewSettings.value,
    requiredPermissionDisplayValue: BillingRoles.canViewSettings.name,
    roleGroup: 'billingRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isBillingDetails: true,
  },
  viewAdmin: {
    name: 'View Admin',
    link: 'view',
    value: 'viewAdmin',
    icon: SettingsApplications,
    requiredPermission: '*',
    requiredPermissionDisplayValue: '*',
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isLayoutRoute: true,
  },
  marketingManager: {
    name: 'Marketing Manager',
    link: 'marketing-manager',
    value: 'marketingManager',
    icon: CampaignOutlined,
    requiredPermission: '*',
    requiredPermissionDisplayValue: '*',
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isViewAdmin: true,
  },
  errorTesting: {
    name: 'Error Testing',
    link: 'error-testing',
    value: 'errorTesting',
    icon: ErrorOutlined,
    requiredPermission: '*',
    requiredPermissionDisplayValue: '*',
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isViewAdmin: true,
  },
  adminLabs: {
    name: 'Admin Labs',
    link: 'admin-labs',
    value: 'adminLabs',
    icon: SettingsOutlined,
    requiredPermission: '*',
    requiredPermissionDisplayValue: '*',
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isViewAdmin: true,
  },
  uiSandbox: {
    name: 'UI Sandbox',
    link: 'ui-sandbox',
    value: 'uiSandbox',
    icon: WidgetsOutlined,
    requiredPermission: '*',
    requiredPermissionDisplayValue: '*',
    roleGroup: 'administrationRoles',
    minimumTier: gPanelTiers.essentials.rank,
    minimumTierDisplayValue: gPanelTiers.essentials.displayValue,
    isViewAdmin: true,
  },
} as const satisfies Record<string, PageConfigItem>;

// Update the type exports
export type PagesConfig = typeof pagesConfig;
export type PagesConfigKey = keyof PagesConfig;
export { type PageConfigItem };

export const allPagesWithLayouts: PageConfigItem[] = Object.keys(
  pagesConfig
).map((page) => pagesConfig[page as PagesConfigKey]);

export const allPages: PageConfigItem[] = allPagesWithLayouts.filter(
  (page) => !page.isLayoutRoute
);

export const userDetailPages = allPages.filter(
  (page) => page.isUserDetails && !page.isGmailSettings
);
export const gmailSettingsPages = allPages.filter(
  (page) => page.isGmailSettings
);
export const groupDetailPages = allPages.filter((page) => page.isGroupDetails);

export const resourceCalendarDetailPages = allPages.filter(
  (page) => page.isCalendarDetails
);

export const decomDetailPages = allPages.filter((page) => page.isDecomDetails);

export const indexingDetailPages = allPages.filter(
  (page) => page.isIndexingDetails
);

export const roleManagerDetailPages = allPages.filter(
  (page) => page.isRoleDetails
);

export const settingsDetailPages = allPages.filter(
  (page) => page.isAppSettings
);

export const billingDetailPages = allPages.filter(
  (page) => page.isBillingDetails
);

export const viewAdminDetailPages = allPages.filter((page) => page.isViewAdmin);
